import * as XLSX from 'xlsx';

// Builds the per-file fingerprint that /upload/ingestion/detect-vendors scores. Parsing happens here,
// in the browser, so a directory drop never ships whole workbooks to the server just to guess a
// vendor — only a header row and a sample of body cells travel.

const SAMPLE_ROWS = 50;      // body rows sampled per file; matches MAX_SAMPLES_PER_COLUMN server-side
const MAX_COLUMNS = 100;     // matches MAX_COLUMNS server-side
const HEADER_SCAN_ROWS = 10; // vendor sheets often carry a title/logo preamble above the real header

// Reading a whole workbook to sample 50 rows is wasteful and can hang the tab on a huge export.
const MAX_PARSE_BYTES = 40 * 1024 * 1024; // xlsx/xls must be unzipped whole; refuse the giants
const CSV_SLICE_BYTES = 512 * 1024;       // csv is line-oriented: the first slice is enough

const TABULAR_RE = /\.(csv|xls|xlsx)$/i;
const CSV_RE = /\.csv$/i;

// react-dropzone sets `path` (with directory traversal); the folder picker sets webkitRelativePath.
// We want the immediate parent directory: "Vendors/Daltile/2026.xlsx" -> "Daltile".
export const folderNameOf = (file) => {
  const path = file.webkitRelativePath || file.path || '';
  const parts = path.split('/').filter(Boolean);
  parts.pop(); // the filename
  return parts.length ? parts[parts.length - 1] : '';
};

export const fileKeyOf = (file, index) =>
  `${file.webkitRelativePath || file.path || file.name}:${file.size}:${index}`;

// Read via FileReader rather than Blob.text()/Blob.arrayBuffer(). Those are unavailable in Safari
// before 14 (and in the jsdom build CRA tests run under), and silently falling back to "no columns"
// there would quietly disable content-based vendor detection for those users.
const readBlob = (blob, as) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(reader.error || new Error('file read failed'));
  if (as === 'text') reader.readAsText(blob);
  else reader.readAsArrayBuffer(blob);
});

const readCsvText = async (file) => {
  const slice = file.size > CSV_SLICE_BYTES ? file.slice(0, CSV_SLICE_BYTES) : file;
  const text = await readBlob(slice, 'text');
  // A truncated slice usually ends mid-row; drop the final partial line so it is not sampled.
  if (file.size > CSV_SLICE_BYTES) {
    const cut = text.lastIndexOf('\n');
    return cut > 0 ? text.slice(0, cut) : text;
  }
  return text;
};

const pad2 = (n) => String(n).padStart(2, '0');

// Rewrite date cells to ISO strings before formatting: with raw:false, sheet_to_json renders
// dates in the cell's DISPLAY format (commonly m/d/yy), which turns a stored 2999-12-30 into the
// ambiguous "12/30/99". Local components on purpose — SheetJS materializes dates in local time,
// so toISOString() would shift a day for positive-UTC-offset users.
export const forceIsoDateCells = (ws) => {
  for (const addr of Object.keys(ws)) {
    if (addr.startsWith('!')) continue;
    const cell = ws[addr];
    if (cell && cell.t === 'd' && cell.v instanceof Date) {
      const d = cell.v;
      ws[addr] = { t: 's', v: `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}` };
    }
  }
};

const sheetNameMatches = (a, b) =>
  String(a ?? '').trim().toLowerCase() === String(b ?? '').trim().toLowerCase();

const rowsFromWorkbook = (workbook, sheetName) => {
  const pickedName = sheetName
    ? workbook.SheetNames.find((n) => sheetNameMatches(n, sheetName))
    : undefined;
  const sheet = workbook.Sheets[pickedName ?? workbook.SheetNames[0]];
  if (!sheet) return [];
  forceIsoDateCells(sheet);
  return XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false, defval: '', raw: false });
};

/**
 * List a spreadsheet's non-empty tabs with (capped) row counts, for the upload dialog's tab
 * picker. Template workbooks ship instruction/example/code tabs beside the real price list —
 * without a pick, every one of them gets processed as pricing. Empty array = not a multi-tab
 * case (CSV, PDF, unparseable, or a single data tab).
 */
export const listDataSheets = async (file) => {
  if (!TABULAR_RE.test(file.name) || CSV_RE.test(file.name)) return [];
  if (file.size > MAX_PARSE_BYTES) return [];
  try {
    const buffer = await readBlob(file, 'buffer');
    const workbook = XLSX.read(new Uint8Array(buffer), { type: 'array', sheetRows: SAMPLE_ROWS + HEADER_SCAN_ROWS, cellDates: true });
    const sheets = [];
    for (const name of workbook.SheetNames) {
      const rows = XLSX.utils.sheet_to_json(workbook.Sheets[name], { header: 1, blankrows: false, defval: '', raw: true });
      const dataRows = rows.filter((r) => r.some((c) => String(c ?? '').trim())).length;
      if (dataRows > 0) sheets.push({ name, rows: dataRows, capped: dataRows >= SAMPLE_ROWS + HEADER_SCAN_ROWS });
    }
    return sheets;
  } catch {
    return [];
  }
};

const nonEmptyCount = (row) => row.filter((cell) => String(cell ?? '').trim()).length;

// The header is the widest of the first few rows. A title banner ("2026 PRICE LIST") occupies one
// cell; the real header occupies many, so width separates them without guessing at content.
const findHeaderIndex = (rows) => {
  let bestIndex = 0;
  let bestWidth = 0;
  for (let i = 0; i < Math.min(HEADER_SCAN_ROWS, rows.length); i += 1) {
    const width = nonEmptyCount(rows[i]);
    if (width > bestWidth) {
      bestWidth = width;
      bestIndex = i;
    }
  }
  return bestWidth >= 2 ? bestIndex : -1;
};

const toColumns = (rows) => {
  const headerIndex = findHeaderIndex(rows);
  if (headerIndex < 0) return [];

  const header = rows[headerIndex].slice(0, MAX_COLUMNS);
  const body = rows.slice(headerIndex + 1, headerIndex + 1 + SAMPLE_ROWS);

  return header
    .map((rawHeader, col) => ({
      header: String(rawHeader ?? '').trim(),
      // Sampled values are NOT deduplicated: the server weights a vendor column by how many rows
      // agree ("48 of 50 rows say Daltile"), and collapsing duplicates destroys that signal.
      samples: body.map((row) => String(row[col] ?? '').trim()).filter(Boolean),
    }))
    .filter((column) => column.header && column.samples.length);
};

/**
 * One fingerprint per file. Never throws: a file we cannot parse still contributes its filename and
 * folder name, which are real detection signals on their own.
 */
// `sheetName` (optional): fingerprint that tab instead of the first — the upload dialog passes
// the user's tab pick so detection and mapping hints look at the data that will be processed.
export const fingerprintFile = async (file, index, sheetName) => {
  const base = {
    key: fileKeyOf(file, index),
    fileName: file.name,
    folderName: folderNameOf(file),
  };

  // PDFs and images carry no columns. The server falls back to filename/folder, and would use
  // `textSample` if we ever add client-side text extraction.
  if (!TABULAR_RE.test(file.name)) return base;
  if (file.size > MAX_PARSE_BYTES && !CSV_RE.test(file.name)) return base;

  try {
    let workbook;
    if (CSV_RE.test(file.name)) {
      workbook = XLSX.read(await readCsvText(file), { type: 'string', sheetRows: SAMPLE_ROWS + HEADER_SCAN_ROWS });
    } else {
      const buffer = await readBlob(file, 'buffer');
      // cellDates so real date cells surface as Dates for forceIsoDateCells, not display text.
      workbook = XLSX.read(new Uint8Array(buffer), { type: 'array', sheetRows: SAMPLE_ROWS + HEADER_SCAN_ROWS, cellDates: true });
    }
    return { ...base, columns: toColumns(rowsFromWorkbook(workbook, sheetName)) };
  } catch {
    return base;
  }
};

// Parsing is CPU-bound on the main thread; a 200-file directory drop would jank the UI if we mapped
// them all at once. Small concurrency keeps it responsive and lets the caller show progress.
export const fingerprintAll = async (files, { concurrency = 4, onProgress } = {}) => {
  const results = new Array(files.length);
  let cursor = 0;
  let done = 0;

  const worker = async () => {
    for (let i = cursor++; i < files.length; i = cursor++) {
      results[i] = await fingerprintFile(files[i], i);
      done += 1;
      if (onProgress) onProgress(done, files.length);
    }
  };

  await Promise.all(Array.from({ length: Math.min(concurrency, files.length) }, worker));
  return results;
};
